import { useState } from 'react';
import { Loader2, CheckCircle2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ['Pharma', 'Payer', 'Investor', 'Hospital', 'Other'];

export default function RequestAccessForm({ surface }) {
  const [form, setForm] = useState({ name: '', organization: '', email: '', role: 'Pharma' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.email.trim()) { setError('Email is required.'); return; }
    setStatus('submitting');
    try {
      const r = await fetch(`${API}/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, surface }),
      });
      if (!r.ok) throw new Error('failed');
      setStatus('success');
    } catch {
      setStatus('idle');
      setError('Could not submit — please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="border-2 border-emerald-300 bg-emerald-50 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="request-access-success">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <p className="text-sm font-semibold text-emerald-900">Request received — we will be in touch within 48 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-amber-200 bg-white rounded-xl p-5 mb-6" data-testid="request-access-form">
      <h3 className="text-base font-bold text-slate-900 mb-1" style={{ fontFamily: "'Work Sans',sans-serif" }}>Request Access</h3>
      <p className="text-xs text-slate-500 mb-4">Enterprise licensing &amp; pilot enquiries — we respond within 48 hours.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Name <span className="text-red-500">*</span></label>
          <input value={form.name} onChange={set('name')} data-testid="request-access-name" placeholder="Jane Doe"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Organization</label>
          <input value={form.organization} onChange={set('organization')} data-testid="request-access-org" placeholder="Acme Pharma"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Email <span className="text-red-500">*</span></label>
          <input type="email" value={form.email} onChange={set('email')} data-testid="request-access-email" placeholder="jane@acme.com"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Role</label>
          <select value={form.role} onChange={set('role')} data-testid="request-access-role"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-300">
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      {error && <p className="text-xs text-red-600 mt-3" data-testid="request-access-error">{error}</p>}
      <button type="submit" disabled={status === 'submitting'} data-testid="request-access-submit"
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-60 transition-colors">
        {status === 'submitting' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit request
      </button>
    </form>
  );
}
